export interface SubNavItem {
  readonly label: string;
  readonly to: string;
  /** 24×24 stroke path for the item's icon in the rail's pop-out menu. */
  readonly path: string;
}

export interface NavItem {
  readonly label: string;
  /** PrimeIcons class, used by the mobile navigation sidebar. */
  readonly icon: string;
  /** 24×24 stroke path for the rail icon (from the approved design). */
  readonly path: string;
  /** Where the rail button goes; for a section with sub-pages, its first sub-page. */
  readonly to: string;
  /** Sub-pages, listed in the pop-out menu beside the rail button; the section is active on any of them. */
  readonly children?: readonly SubNavItem[];
}

const DESIGN_PATH = 'M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4';
const RESULTS_PATH = 'M4 20h16M7 16v-5M12 16V7M17 16v-8';
const FAVORITES_PATH = 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z';

/**
 * Top-level sections, matching the AllyIQ three-button navigation. Users land on Configure; Home
 * keeps its /dashboard route but has no nav entry. Builder's Results and Favorites are placeholders until those pages are designed.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Configure', icon: 'pi pi-sliders-h', path: 'M4 6h9m4 0h3M4 12h3m4 0h9M4 18h11m4 0h1M15 4v4M9 10v4M17 16v4', to: '/account-settings' },
  {
    label: 'Builder', icon: 'pi pi-th-large', path: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z', to: '/compare',
    children: [
      { label: 'Design', to: '/compare', path: DESIGN_PATH },
      { label: 'Results', to: '/builder/results', path: RESULTS_PATH },
      { label: 'Favorites', to: '/builder/favorites', path: FAVORITES_PATH },
    ],
  },
  {
    label: 'Simulator', icon: 'pi pi-bolt', path: 'M13 3L5 14h6l-1 7 8-11h-6z', to: '/generate',
    children: [
      { label: 'Design', to: '/generate', path: DESIGN_PATH },
      { label: 'Results', to: '/results', path: RESULTS_PATH },
      { label: 'Favorites', to: '/favorites', path: FAVORITES_PATH },
    ],
  },
];

/** The section that owns a path: its own route or one of its sub-pages. */
export const sectionFor = (pathname: string): NavItem | undefined =>
  NAV_ITEMS.find((item) => item.to === pathname || (item.children ?? []).some((child) => child.to === pathname));
